import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { Form } from 'antd';
import { FormInstance } from 'antd/es/form';
// Api
import { GET_CAMPAIGN_OFFERS } from 'api/campaignV2/queries';
import { GET_BOOK_ME_STORES, GET_STORE_INFO } from 'api/store/queries';
// Types
import {
  GetCampaignOffers,
  GetCampaignOffersVariables,
} from 'api/campaignV2/types/GetCampaignOffers';
import {
  GetBookMeStores,
  GetBookMeStoresVariables,
} from 'api/store/types/GetBookMeStores';
import { GetStore, GetStoreVariables } from 'api/store/types/GetStore';
import {
  AdminStoresOrderBy,
  CampaignOfferStatus,
  StoreStatus,
  UserRole,
} from 'api/graphql-global-types';
// Components
import SelectInfiniteScroll, {
  LoadSelectInfiniteScrollOptionsArgs,
  SelectInfiniteScrollOption,
} from 'components/common/SelectInfiniteScroll/SelectInfiniteScroll';
import TalentItem from 'pages/CampaignDetails/CampaignDetails/components/TalentItem/TalentItem';
// Style
import styles from './PreselectedTalentSection.module.scss';

const STORE_ROLES = [UserRole.Athlete, UserRole.ContentCreator];

type PreselectedTalentSectionProps = {
  campaignId: string;
  form: FormInstance;
  onTalentChange: (talentId: string | null) => void;
  locked?: boolean;
};

const getStoreLabel = (store: {
  firstName: string;
  lastName: string;
  storeDetails?: { storeName?: string | null } | null;
}): string =>
  store.storeDetails?.storeName || `${store.firstName} ${store.lastName}`;

const PreselectedTalentSection = ({
  campaignId,
  form,
  onTalentChange,
  locked = false,
}: PreselectedTalentSectionProps): JSX.Element => {
  const client = useApolloClient();
  const [selectedTalentId, setSelectedTalentId] = useState<string | null>(null);

  const { data: offersData } = useQuery<
    GetCampaignOffers,
    GetCampaignOffersVariables
  >(GET_CAMPAIGN_OFFERS, {
    variables: {
      input: {
        campaignId,
        limit: 1,
        statusesV2: [CampaignOfferStatus.PROPOSED],
      },
    },
    fetchPolicy: 'network-only',
    skip: !campaignId,
  });

  const { data: storeInfo } = useQuery<GetStore, GetStoreVariables>(
    GET_STORE_INFO,
    {
      variables: {
        input: { id: selectedTalentId as string },
      },
      fetchPolicy: 'cache-and-network',
      skip: !selectedTalentId,
    }
  );

  // Hydrate from the existing PROPOSED offer (Direct Booking has no search criteria).
  useEffect(() => {
    const offerTalentId = offersData?.getCampaignOffers?.entities?.[0]?.userId;
    if (!offerTalentId || selectedTalentId) return;

    setSelectedTalentId(offerTalentId);
    onTalentChange(offerTalentId);
    form.setFieldsValue({ talentId: offerTalentId });
  }, [offersData, selectedTalentId, onTalentChange, form]);

  const loadOptions = useCallback(
    async ({ search, offset, limit }: LoadSelectInfiniteScrollOptionsArgs) => {
      const { data } = await client.query<
        GetBookMeStores,
        GetBookMeStoresVariables
      >({
        query: GET_BOOK_ME_STORES,
        fetchPolicy: 'network-only',
        variables: {
          storeRoles: STORE_ROLES,
          input: {
            ...(search && { storeName: search }),
            orderBy: AdminStoresOrderBy.storeName,
            status: [StoreStatus.Active],
            isOnboarded: true,
            limit,
            offset,
          },
        },
      });

      return {
        options: (data?.adminStores.entities ?? []).map((store) => ({
          value: store.id,
          label: getStoreLabel(store),
        })),
        total: data?.adminStores.total ?? 0,
      };
    },
    [client]
  );

  const handleSelectStore = (value: string): void => {
    setSelectedTalentId(value);
    onTalentChange(value);
  };

  const selectedStore = storeInfo?.store;

  const extraOptions = useMemo<SelectInfiniteScrollOption[]>(() => {
    if (!selectedStore) return [];
    return [
      {
        value: selectedStore.id,
        label: getStoreLabel(selectedStore),
      },
    ];
  }, [selectedStore]);

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Talent</h3>

      <div className={styles.selectWrapper}>
        <Form.Item
          name="talentId"
          rules={[{ required: true, message: 'This field is required' }]}
          label="Select talent"
          extra={
            locked
              ? 'The agency has already paid for this offer, so the talent cannot be changed in the current status. It is only editable while the campaign is in draft status.'
              : undefined
          }
        >
          <SelectInfiniteScroll
            id="talentId"
            placeholder="Select talent"
            loadOptions={loadOptions}
            extraOptions={extraOptions}
            onSelect={handleSelectStore}
            disabled={locked}
          />
        </Form.Item>
      </div>

      {selectedStore ? (
        <div className={styles.talent}>
          <TalentItem talent={selectedStore} isVertical />
        </div>
      ) : null}
    </section>
  );
};

export default PreselectedTalentSection;
